import { useMemo, useState } from 'react';

import { useIsInternalProject } from '@/features/projects/project-details/hooks/use-is-internal-project';

type UseContactItemLogicParams = {
  projectType: string;
  projectOrgId: number | null;
  mainContact: string | null;
  contractorContact: string | null;
  clientContact: string | null;
};

type ContactField = {
  field: 'main_contact' | 'contractor_contact' | 'client_contact';
  contact: string | null;
  label: string;
  isEditable: boolean;
};

export function useContactItemLogic({
  projectType,
  projectOrgId,
  mainContact,
  contractorContact,
  clientContact,
}: UseContactItemLogicParams) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeField, setActiveField] = useState<string>('');
  const [activeContact, setActiveContact] = useState<string | null>(null);
  const [activeLabel, setActiveLabel] = useState<string>('');

  // Check if this is an internal project
  const isInternal = useIsInternalProject(projectOrgId);

  // Project type flags
  const isSubproject = useMemo(
    () =>
      projectType === 'subproject_client' ||
      projectType === 'subproject_contractor',
    [projectType],
  );

  const isClientView = useMemo(
    () => projectType === 'subproject_client',
    [projectType],
  );

  const isContractorView = useMemo(
    () => projectType === 'subproject_contractor',
    [projectType],
  );

  const handleClick = (
    field: string,
    contact: string | null,
    label: string,
  ) => {
    setActiveField(field);
    setActiveContact(contact);
    setActiveLabel(label);
    setDialogOpen(true);
  };

  // Computed contact fields configuration
  const contactFields = useMemo<ContactField[]>(() => {
    if (isSubproject) {
      return [
        {
          field: 'contractor_contact' as const,
          contact: contractorContact,
          label: 'contractor_contact',
          // Editable if: contractor view OR internal project
          isEditable: isContractorView || isInternal,
        },
        {
          field: 'client_contact' as const,
          contact: clientContact,
          label: 'client_contact',
          // Editable if: client view OR internal project
          isEditable: isClientView || isInternal,
        },
      ];
    }

    return [
      {
        field: 'main_contact' as const,
        contact: mainContact,
        label: 'main_contact',
        isEditable: true,
      },
    ];
  }, [
    isSubproject,
    isClientView,
    isContractorView,
    isInternal,
    mainContact,
    contractorContact,
    clientContact,
  ]);

  return {
    // Project type flags
    isSubproject,
    isClientView,
    isContractorView,
    // Dialog management
    dialogOpen,
    setDialogOpen,
    activeField,
    activeContact,
    activeLabel,
    handleClick,
    // Computed properties
    contactFields,
  };
}
